import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { users } from '../../services/api';
import NotificationBell from '../notifications/NotificationBell';
import Logo from '../common/Logo';
import { nomUsuari, inicialUsuari } from '../../utils/usuari';

function Layout() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Mobile sidebar open/close
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Desktop sidebar collapsed state (persisted)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Pending users count (admin only)
  const [pendingCount, setPendingCount] = useState(0);

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Load pending count for admins
  useEffect(() => {
    if (user?.role === 'admin') {
      users.getPendingCount()
        .then(res => setPendingCount(res.data.count))
        .catch(() => setPendingCount(0));
    }
  }, [user?.role]);

  // S'espera el logout abans de navegar: si no, el canvi de ruta desmuntaria el
  // component i podria avortar la petició que invalida la sessió al servidor.
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { name: 'Projectes', path: '/projects', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
  ];

  // Add "My Grades" for analysts
  if (user?.role === 'analyst') {
    navigation.push({ name: 'Qualificacions', path: '/my-grades', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' });
  }

  // Add notifications for all users
  navigation.push({ name: 'Notificacions', path: '/notifications', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' });

  if (user?.role === 'admin') {
    navigation.push({ name: 'Usuaris', path: '/users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', badge: pendingCount });
  }

  // Close mobile sidebar when navigating
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-surface-subtle">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Light in light mode, dark in dark mode */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-surface border-r border-border transform transition-all duration-200 ease-in-out flex flex-col
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}
          w-64
        `}
      >
        {/* Header with Logo */}
        <div className="flex items-center justify-between h-14 bg-accent px-3">
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : ''}`}>
            {/* Marca d'ARGOS: el marc. Vegeu components/common/Logo.js */}
            <Logo className="flex-shrink-0 w-8 h-8 text-white" />
            {!sidebarCollapsed && (
              <span className="ml-3 text-white text-lg font-semibold tracking-wide">ARGOS</span>
            )}
          </div>
          {/* Collapse toggle - desktop only (only when expanded) */}
          {!sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="hidden lg:block p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded transition-colors"
              title="Reduir menú"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="mt-4 flex-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center py-2.5 text-content-secondary hover:bg-surface-muted hover:text-content transition-colors relative group
                  ${isActive ? 'bg-accent-subtle text-accent-hover border-l-3 border-primary-600' : 'border-l-3 border-transparent'}
                  ${sidebarCollapsed ? 'px-4 justify-center' : 'px-5'}
                `}
                // `aria-label` i no `title`: el `title` pintava el tooltip NATIU del
                // navegador a sobre del nostre, amb retard i amb un altre estil, o
                // sigui que amb el menú plegat en sortien dos alhora. Treure'l del
                // tot tampoc va bé —plegat, l'enllaç només té una icona i es
                // quedaria sense nom per a un lector de pantalla, perquè el nostre
                // tooltip va amb `invisible` i això l'exclou de l'arbre
                // d'accessibilitat—. `aria-label` dona el nom sense pintar res.
                aria-label={item.name}
              >
                <div className="relative">
                  <svg className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-accent' : ''} ${sidebarCollapsed ? '' : 'mr-3'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                  {/* Badge on icon when collapsed */}
                  {sidebarCollapsed && item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 text-[10px] font-bold bg-amber-500 text-white rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                {!sidebarCollapsed && (
                  <>
                    <span className="truncate text-sm font-medium">{item.name}</span>
                    {/* Badge next to name when expanded */}
                    {item.badge > 0 && (
                      <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-amber-500 text-white rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
                {/* Tooltip amb el menú plegat.

                    El text va amb `text-content` i no amb `text-white`: en mode
                    fosc `bg-surface` és #22262B i el blanc s'hi llegia, però en
                    mode clar és blanc pur, o sigui que era text blanc sobre fons
                    blanc i el tooltip no es veia. `text-content` canvia amb el
                    tema; el blanc fix, no. La vora i l'ombra separen el tooltip
                    de la barra, que també és `bg-surface`. */}
                {sidebarCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-surface text-content border border-border shadow-lg text-sm rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50">
                    {item.name}
                    {item.badge > 0 && ` (${item.badge})`}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Expand button when collapsed */}
        {sidebarCollapsed && (
          <div className="p-3 border-t border-border flex justify-center">
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="hidden lg:block p-2 text-content-disabled hover:text-content-secondary hover:bg-surface-muted rounded transition-colors"
              title="Expandir menú"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-200 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Top bar */}
        <header className="h-14 bg-surface border-b border-border flex items-center justify-between px-4 lg:px-6">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 text-content-muted hover:text-content-secondary hover:bg-surface-muted rounded-md transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side actions */}
          <div className="flex items-center space-x-1">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 text-content-muted hover:text-content-secondary hover:bg-surface-muted rounded-md transition-colors"
              title={isDark ? 'Canviar a mode clar' : 'Canviar a mode fosc'}
            >
              {isDark ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {/* Notification bell */}
            <NotificationBell />

            {/* User menu */}
            <div className="flex items-center ml-3 pl-3 border-l border-border">
              <Link
                to="/profile"
                className="flex items-center hover:opacity-80 transition-opacity"
                title="El meu perfil"
              >
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white text-sm font-medium">
                  {inicialUsuari(user)}
                </div>
                <span className="hidden sm:block ml-2 text-sm font-medium text-content-secondary">
                  {nomUsuari(user)}
                </span>
              </Link>
              <button
                onClick={handleLogout}
                className="ml-2 p-2 text-content-muted hover:text-content-secondary hover:bg-surface-muted rounded-md transition-colors"
                title="Tancar sessió"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
